import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
  Attention100,
  Attention800,
  Error100,
  Gray200,
  Gray900,
  Info100,
  Info800,
  Primary500,
  White,
} from '@mehilainen/design-system-tokens/colors'
import type React from 'react'

import { NotificationStyle } from '../../../__generated__/api'
import { CenteredRowFlex } from '../../../common/components/Layout/Layout'
import { useIsMobile } from '../../../common/hooks/useBreakpoint'
import { scale } from '../../utils/scale'

const ExternalIcon = styled.div<{ size: string; url: string }>`
  min-width: ${({ size }) => size};
  min-height: ${({ size }) => size};
  background: url(${({ url }) => url}) no-repeat 50% 50%;
  background-size: cover;
`

export const HeadingTextColor: Record<NotificationStyle, string> = {
  [NotificationStyle.Info]: Info800,
  [NotificationStyle.Raise]: Primary500,
  [NotificationStyle.WalkIn]: Primary500,
  [NotificationStyle.Warning]: Attention800,
  [NotificationStyle.Error]: Attention800,
}

const BodyTextColor: Record<NotificationStyle, string> = {
  [NotificationStyle.Info]: Info800,
  [NotificationStyle.Raise]: Gray900,
  [NotificationStyle.WalkIn]: Gray900,
  [NotificationStyle.Warning]: Gray900,
  [NotificationStyle.Error]: Gray900,
}

export const BackgroundColor: Record<NotificationStyle, string> = {
  [NotificationStyle.Info]: Info100,
  [NotificationStyle.Raise]: White,
  [NotificationStyle.WalkIn]: White,
  [NotificationStyle.Warning]: Attention100,
  [NotificationStyle.Error]: Error100,
}

const IconFilter: Record<NotificationStyle, string> = {
  [NotificationStyle.Info]:
    'brightness(0) saturate(100%) invert(10%) sepia(27%) saturate(6377%) hue-rotate(188deg) brightness(98%) contrast(103%)',
  [NotificationStyle.Raise]:
    'brightness(0) saturate(100%) invert(27%) sepia(89%) saturate(1000%) hue-rotate(112deg) brightness(100%) contrast(101%)',
  [NotificationStyle.WalkIn]:
    'brightness(0) saturate(100%) invert(27%) sepia(89%) saturate(1000%) hue-rotate(112deg) brightness(100%) contrast(101%)',
  [NotificationStyle.Warning]:
    'brightness(0) saturate(100%) invert(12%) sepia(75%) saturate(2094%) hue-rotate(38deg) brightness(94%) contrast(102%)',
  [NotificationStyle.Error]:
    'brightness(0) saturate(100%) invert(12%) sepia(75%) saturate(2094%) hue-rotate(38deg) brightness(94%) contrast(102%)',
}

const Container = styled(CenteredRowFlex)<{
  isMobile: boolean
  notificationStyle: NotificationStyle
}>`
  align-items: start;
  padding: ${scale(1.5)};
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${scale(2)};

  color: ${({ notificationStyle }) => BodyTextColor[notificationStyle]};
  background: ${({ notificationStyle }) => BackgroundColor[notificationStyle]};
  border-radius: ${scale(0.5)};
  ${({ notificationStyle }) =>
    notificationStyle === NotificationStyle.Raise &&
    css`
      border: 1px solid ${Gray200};
    `}

  h2 {
    color: ${({ notificationStyle }) => HeadingTextColor[notificationStyle]};
  }

  ${ExternalIcon}, svg {
    filter: ${({ notificationStyle }) => IconFilter[notificationStyle]};
  }
`

interface Props {
  style: NotificationStyle
  className?: string
}

const Notification: React.FC<Props> = ({ style, children, className }) => {
  const isMobile = useIsMobile()

  return (
    <Container
      as="section"
      notificationStyle={style ?? NotificationStyle.Info}
      isMobile={isMobile}
      className={className}
    >
      {children}
    </Container>
  )
}

export default Notification
